import accountClient from '@/services/AccountClient'
import clientRequestsClient from '@/services/ClientRequestsClient'
import portalClient from '@/services/PortalClient'
import Account from '@/models/Account'
import User from '@/models/AccountUser'
import { HEADER_SKIP_AUTOEXTEND } from '@/services/ApiClient/ApiClient'

export default {
  fetchAll (params = {}, options) {
    const defaultSort = 'createdAt,desc'

    return accountClient.sendRequest(
      'GET',
      'api/admin/v1/profiles',
      {
        ...params,
        sort: (params.sort != null)
          ? [params.sort, defaultSort]
          : defaultSort
      },
      options
    )
  },
  fetchById (id) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${id}`
    )
  },
  updateAccountProfile (id, data) {
    return accountClient.sendRequest(
      'PUT',
      `api/admin/v1/profiles/${id}`,
      data
    )
  },
  fetchAccounts (profileId, params = { size: 100 }, options) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/wallets`,
      params,
      options
    )
  },
  fetchUsersFromPortal (profileId) {
    const defaultSort = 'created'

    return portalClient
      .sendRequest(
        'GET',
        `admin/api/v2/profiles/${profileId}`,
        { sort: defaultSort }
      )
      .then(({ payload }) => ({ payload: payload.users }))
  },
  fetchUsersFromAccount (profileId) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/users`
    )
  },
  fetchPortalProfile (profileId) {
    return portalClient.sendRequest(
      'GET',
      `admin/api/v1/profiles/${profileId}`
    )
  },
  fetchRestrictedCountries (profileId) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/restricted-countries`
    )
  },
  updateRestrictedCountries (profileId, type, countries) {
    return accountClient.sendRequest(
      'PUT',
      `api/admin/v1/profiles/${profileId}/restricted-countries`,
      {
        type,
        countries
      }
    )
  },
  updatePortalProfile (profileId, data) {
    return portalClient.sendRequest(
      'PUT',
      `admin/api/v2/profiles/${profileId}/settings`,
      data
    )
  },
  fetchDisabledFees (profileId) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/disabled-fees`
    )
  },
  fetchTurnoverLimits (profileId) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/turnover-limits`
    )
  },
  fetchProfileTierLimits (profileId) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/tier-limits/${profileId}`
    )
  },
  fetchClientRequests (profileId, params, options) {
    const { silent, ...queryParams } = params

    return clientRequestsClient.sendRequest(
      'GET',
      `api/v1/admin/profiles/${profileId}/requests`,
      { ...queryParams, service: 'bilder-account' },
      silent != null
        ? { headers: { [HEADER_SKIP_AUTOEXTEND]: true }, ...options }
        : options
    )
  },
  increaseTurnoverLimits (profileId, reason) {
    return accountClient.sendRequest(
      'POST',
      `api/admin/v1/profiles/${profileId}/turnover-limits`,
      { reason }
    )
  },
  resetTurnoverLimits (profileId, reason) {
    return accountClient.sendRequest(
      'DELETE',
      `api/admin/v1/profiles/${profileId}/turnover-limits`,
      { reason }
    )
  },
  disableFee (profileId, data) {
    return accountClient.sendRequest(
      'POST',
      `api/admin/v1/profiles/${profileId}/disabled-fees`,
      data
    )
  },
  enableFee (profileId, feeType) {
    return accountClient.sendRequest(
      'DELETE',
      `api/admin/v1/profiles/${profileId}/disabled-fees/${feeType}`
    )
  },
  fetchCustomers (profileId, params = {}) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/profile-customers`,
      params
    )
  },
  fetchAllCustomers (profileId, params = {}) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/profile-customers`,
      params
    )
  },
  createCustomer (profileId, data) {
    return accountClient.sendRequest(
      'POST',
      `api/admin/v1/profiles/${profileId}/profile-customers?walletId=${data.walletId}&channel=${data.channel}`,
      data.profileCustomerRequest
    )
  },
  createCustomers (profileId, data) {
    return accountClient.sendRequest(
      'POST',
      `api/admin/v1/profiles/${profileId}/profile-customers/batch?walletId=${data.walletId}&channel=${data.channel}`,
      data.request
    )
  },
  fetchForexCurrencyPairs (profileId, params) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/fx-rates`,
      params
    )
  },
  saveForexCurrencyPair (profileId, data) {
    return accountClient.sendRequest(
      'PUT',
      `api/admin/v1/profiles/${profileId}/fx-rates`,
      data
    )
  },
  deleteForexCurrencyPair (profileId, fromCurrency, toCurrency) {
    return accountClient.sendRequest(
      'DELETE',
      `api/admin/v1/profiles/${profileId}/fx-rates/${fromCurrency}/${toCurrency}`
    )
  },
  fetchForexCurrencyLimits (profileId, params) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/fx-currency-limits`,
      params
    )
  },
  saveForexCurrencyLimits (profileId, data) {
    return accountClient.sendRequest(
      'PUT',
      `api/admin/v1/profiles/${profileId}/fx-currency-limits`,
      data
    )
  },
  deleteForexCurrencyLimits (profileId, currency) {
    return accountClient.sendRequest(
      'DELETE',
      `api/admin/v1/profiles/${profileId}/fx-currency-limits/${currency}`
    )
  },
  fetchApprovalSettings (profileId, options) {
    return accountClient.sendRequest(
      'GET',
      `api/admin/v1/profiles/${profileId}/payment-approval-settings`,
      options
    )
  }
}

export function ProfileAccountsApiPayload (payload) {
  return payload.map(accountData => new Account(accountData))
}

export function ProfileUsersApiPayload (payload) {
  return payload.map(userData => new User(userData))
}
